import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { environment } from '../../environments/environment';
import { GenericEvent, GenericProduct } from '../models/event.model';
import { TokenDecodeResponse, RegisterableEventResponse } from '../models/backend-response.model';

@Injectable({
  providedIn: 'root'
})
export class EventService {
  private apiUrl = environment.apiUrl;
  private companyId = environment.companyId;

  constructor(private http: HttpClient) {}

  decodeToken(token: string): Observable<GenericEvent> {
    const url = `${this.apiUrl}/api/event_tokens/decode`;
    return this.http.post<TokenDecodeResponse>(url, { token, type: 'attendee_register' }).pipe(
      map(response => {
        const eventData = response.event;

        if (!eventData) {
          throw new Error('Event data not found in token decode response');
        }

        const topicName = typeof eventData.topic === 'string'
          ? eventData.topic
          : eventData.topic?.display_name || eventData.topic?.name;
        const topicDescription = typeof eventData.topic === 'string'
          ? undefined
          : eventData.topic?.description || eventData.topic?.info;
        const topicIsi = typeof eventData.topic === 'string'
          ? undefined
          : (() => {
              const blocks = (eventData.topic as Exclude<typeof eventData.topic, string>)?.products
                ?.filter(p => p.isi_message || p.isi || p.pi)
                .map(p => {
                  const links = [
                    p.isi ? `<a href="${p.isi}" target="_blank" rel="noopener noreferrer">${p.name} &mdash; isi</a>` : null,
                    p.pi  ? `<a href="${p.pi}"  target="_blank" rel="noopener noreferrer">${p.name} &mdash; pi</a>`  : null,
                  ].filter(Boolean).join('<br>');
                  return `<p><strong>${p.name}:</strong></p>${p.isi_message || ''}${links ? `<p>${links}</p>` : ''}`;
                });
              return blocks?.length ? blocks.join('<hr>') : undefined;
            })();

        const topicProducts: GenericProduct[] = typeof eventData.topic === 'string'
          ? []
          : (eventData.topic?.products ?? []).map(p => ({
              id: p.id,
              name: p.name,
              isi: p.isi,
              pi: p.pi,
              image: p.image,
              wide_image: p.wide_image
            }));

        const meetingTypeName = typeof eventData.meeting_type === 'string'
          ? eventData.meeting_type
          : eventData.meeting_type?.name;

        const isVirtual = meetingTypeName
          ? meetingTypeName.toLowerCase().includes('virtual') || meetingTypeName.toLowerCase().includes('webcast')
          : false;

        const venue = eventData.venue;
        let googleMapsUri = venue?.google_maps_uri;
        if (!googleMapsUri && venue) {
          googleMapsUri = this.buildGoogleMapsUri(venue);
        }

        const mapped: GenericEvent = {
          id: eventData.id || response.eventId || 0,
          companyId: response.companyId || 0,
          name: eventData.name || '',
          topic: topicName,
          topicDescription: topicDescription,
          topicIsi: topicIsi,
          topicProducts: topicProducts.length ? topicProducts : undefined,
          eventDisplayId: eventData.name || undefined,
          startAt: eventData.start_at,
          endAt: eventData.end_at,
          friendlyDate: eventData.start_at
            ? this.formatFriendlyDate(eventData.start_at, eventData.timezone_id)
            : undefined,
          startTime: eventData.start_at
            ? this.formatEventTime(eventData.start_at, eventData.timezone_id)
            : undefined,
          timezone: eventData.timezone_id,
          meetingType: meetingTypeName,
          isVirtual,
          webcastLink: eventData.webcast_link,
          venue: venue ? {
            name: venue.name || '',
            address1: venue.address1,
            address2: venue.address2,
            city: venue.city,
            state: venue.state,
            zip: venue.zip,
            googleMapsUri: googleMapsUri,
            latitude: venue.latitude,
            longitude: venue.longitude
          } : undefined,
          speakers: eventData.speakers?.filter(s => ['confirmed'].includes(s.status?.toLowerCase() ?? '')).map(s => ({
            id: s.id,
            firstName: s.first_name,
            lastName: s.last_name,
            fullName: `${s.first_name || ''} ${s.last_name || ''}`.trim(),
            degrees: Array.isArray(s.degrees) ? s.degrees.filter(Boolean).join(', ') : s.degrees,
            title: s.title,
            affiliation: s.affiliation,
            photo: s.photo,
            role: 'Speaker'
          })) ?? [],
          hosts: eventData.client ? [{
            firstName: eventData.client.first_name || '',
            lastName: eventData.client.last_name || '',
            fullName: `${eventData.client.first_name || ''} ${eventData.client.last_name || ''}`.trim(),
            title: eventData.client.title,
            company: eventData.client.company_name,
            photo: eventData.client.photo,
            role: 'Host'
          }] : [],
          company: eventData.company ? {
            id: eventData.company.id || response.companyId || 0,
            name: eventData.company.name || '',
            image: eventData.company.image,
            wide_image: eventData.company.wide_image
          } : undefined,
          token: token,
          attendee: response.attendee,
          attendeeId: response.attendeeId ?? response.attendee?.id,
          eventStatusId: eventData.status_id ?? eventData.status?.id,
          invitedStatusId: response.attendee?.invited_status_id,
          attendeeStatusId: response.attendee?.attendee_status?.id,
          attendeeStatusLabel: response.attendee?.attendee_status?.label
        };

        return mapped;
      })
    );
  }

  /** Exchange an invite (attendee) token for an event-only register token. */
  getEventOnlyToken(token: string): Observable<{ id: number; token: string }> {
    const url = `${this.apiUrl}/api/event_tokens/event_only`;
    return this.http.post<{ id: number; token: string }>(url, { token }).pipe(
      map(response => {
        if (!response?.token) {
          throw new Error('Unable to create a generic registration link.');
        }
        return { id: response.id, token: response.token };
      })
    );
  }

  searchEventByCode(code: string): Observable<{ id: number; token: string } | null> {
    const url = `${this.apiUrl}/api/companies/${this.companyId}/events/registerable`;
    const normalizedCode = code.trim().toUpperCase();

    return this.http.post<RegisterableEventResponse>(url, { name: normalizedCode }).pipe(
      map(response => {
        if (!response || !response.id || !response.token) {
          return null;
        }
        return { id: response.id, token: response.token };
      })
    );
  }

  getRegistrationQuestions(companyId: number | string, eventId: number | string, token?: string): Observable<any[]> {
    let url = `${this.apiUrl}/api/companies/${companyId}/events/${eventId}/evaluations/registration_anonymous`;
    if (token) {
      url += `?token=${encodeURIComponent(token)}`;
    }
    return this.http.get<any[]>(url);
  }

  getCompanyDropdown(companyId: number | string, name: string): Observable<Array<{ value: string; label: string }>> {
    const url = `${this.apiUrl}/api/companies/${companyId}/dropdowns/${name}`;
    return this.http.get<Array<{ value: string; label: string }>>(url);
  }

  getCompanyHcpDropdown(companyId: number | string, name: string): Observable<Array<{ value: string; label: string }>> {
    const url = `${this.apiUrl}/api/companies/${companyId}/hcp_dropdowns/${name}`;
    return this.http.get<Array<{ value: string; label: string }>>(url);
  }

  getCommonDropdown(name: string): Observable<Array<{ value: string; label: string }>> {
    const url = `${this.apiUrl}/api/dropdowns/${name}`;
    return this.http.get<Array<{ value: string; label: string }>>(url);
  }

  private formatEventTime(startAt: string, timezoneId?: string): string {
    if (!startAt) return '';
    const date = new Date(startAt);
    const options: Intl.DateTimeFormatOptions = {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    };
    if (timezoneId) {
      options.timeZone = timezoneId;
    }
    try {
      return date.toLocaleString('en-US', options);
    } catch {
      return date.toLocaleString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    }
  }

  private formatFriendlyDate(dateString: string, timezoneId?: string): string {
    const date = new Date(dateString);
    const options: Intl.DateTimeFormatOptions = {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    };
    if (timezoneId) {
      options.timeZone = timezoneId;
    }
    try {
      return date.toLocaleDateString('en-US', options);
    } catch {
      return date.toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    }
  }

  private buildGoogleMapsUri(venue: { latitude?: number; longitude?: number; address1?: string; city?: string; state?: string; zip?: string }): string | undefined {
    if (venue.latitude && venue.longitude) {
      return `https://www.google.com/maps/search/?api=1&query=${venue.latitude},${venue.longitude}`;
    }
    if (venue.address1 && venue.city && venue.state) {
      const query = encodeURIComponent(`${venue.address1}, ${venue.city}, ${venue.state} ${venue.zip || ''}`);
      return `https://www.google.com/maps/search/?api=1&query=${query}`;
    }
    return undefined;
  }
}
